/**
 * useLiveSyncPolling — silently re-fetches data every N ms.
 *
 * Designed for the Service Requests page: polls the list endpoint and,
 * when a detail drawer is open, also refreshes the currently-selected item.
 * Detects `pending_payment → final_processing` transitions and fires a toast
 * so the admin sees payment landings even without focus on the drawer.
 *
 * Usage:
 *   const { lastSyncAt, active, toggle } = useLiveSyncPolling({
 *     enabled: true,
 *     intervalMs: 8000,
 *     onPoll: async () => fetchRequests({ silent: true }),
 *     onRefreshSelected: selectedId ? () => refreshSelected(selectedId) : null,
 *   });
 */
import { useEffect, useRef, useState } from 'react';

export const LIVE_SYNC_INTERVAL_MS = 8000;

export function useLiveSyncPolling({
  enabled = true,
  intervalMs = LIVE_SYNC_INTERVAL_MS,
  onPoll,
  onRefreshSelected,
  deps = [],
}) {
  const [active, setActive] = useState(enabled);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const onPollRef = useRef(onPoll);
  const onRefreshRef = useRef(onRefreshSelected);

  // Keep refs fresh so the interval always calls the latest callbacks
  useEffect(() => { onPollRef.current = onPoll; }, [onPoll]);
  useEffect(() => { onRefreshRef.current = onRefreshSelected; }, [onRefreshSelected]);

  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(async () => {
      try { await onPollRef.current?.(); } catch { /* silent */ }
      try { await onRefreshRef.current?.(); } catch { /* silent */ }
      setLastSyncAt(new Date());
    }, intervalMs);
    return () => clearInterval(id);
  }, [active, intervalMs, ...deps]);

  return {
    active,
    lastSyncAt,
    setLastSyncAt,
    toggle: () => setActive((prev) => !prev),
  };
}
